import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import CurrencyMaskInput from "@/components/atoms/CurrencyMaskInput";
import useGetAvailablePaymentTypes from "@/hooks/requests/t2Pay/use-get-available-payment-types";
import {
  AvailablePaymentType,
  PaymentType,
} from "@/interfaces/payment-type.interface";
import { PaymentTypeMessages } from "@/messages/platforms/talk2PayHome/payment-type.messages";

import {
  buildPaymentTypeInitialData,
  buildPaymentTypeSchema,
  CREDIT_CARD_PAYMENT_TYPE_NAME,
  PaymentTypeFormData,
  serializeMonthlyInterestRates,
} from "../../form-data";

type Props = {
  messages: PaymentTypeMessages;
  isEditing: boolean;
  initial?: PaymentType | null;
  onSubmit: (values: PaymentTypeFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
};

const resizeInterestRates = (rates: number[], maxCount: number): number[] =>
  Array.from({ length: Math.max(1, maxCount) }, (_, index) => Number(rates[index]) || 0);

const isCreditCardOption = (option?: AvailablePaymentType | null) =>
  option?.name?.toLowerCase() === CREDIT_CARD_PAYMENT_TYPE_NAME.toLowerCase();

const PaymentTypeForm = ({ messages, isEditing, initial, onSubmit, onCancel, isLoading }: Props) => {
  const { data: available, isLoading: isLoadingAvailable } = useGetAvailablePaymentTypes(!isEditing);

  const options: AvailablePaymentType[] = isEditing
    ? initial
      ? [{ id: initial.paymentTypesID, name: initial.paymentTypeName }]
      : []
    : available ?? [];

  return (
    <Formik
      initialValues={buildPaymentTypeInitialData(initial)}
      enableReinitialize
      validate={(values) => {
        const selectedOption = options.find((option) => option.id === values.paymentTypesID);
        const schema = buildPaymentTypeSchema(messages, isCreditCardOption(selectedOption));
        try {
          schema.validateSync(values, { abortEarly: false });
          return {};
        } catch (error: any) {
          const formErrors: Record<string, any> = {};
          if (error?.inner) {
            error.inner.forEach((item: any) => {
              if (item.path && !formErrors[item.path]) {
                formErrors[item.path] = item.message;
              }
            });
          }
          return formErrors;
        }
      }}
      onSubmit={(values) => {
        const selectedOption = options.find((option) => option.id === values.paymentTypesID);
        const isCreditCard = isCreditCardOption(selectedOption);
        const maxInstallmentCount = isCreditCard ? Number(values.maxInstallmentCount) || 1 : 1;
        const interestRates = isCreditCard
          ? resizeInterestRates(values.interestRates ?? [], maxInstallmentCount)
          : [0];

        onSubmit({
          paymentTypesID: Number(values.paymentTypesID),
          active: true,
          discountApplied: Number(values.discountApplied) || 0,
          discountOver: Number(values.discountOver) || 0,
          maxInstallmentCount,
          minInstallmentAmount: isCreditCard ? Number(values.minInstallmentAmount) || 0 : 0,
          monthlyInterestRates: isCreditCard
            ? serializeMonthlyInterestRates(interestRates, maxInstallmentCount)
            : "{}",
          interestRates,
        });
      }}
    >
      {({ values, errors, handleChange, setFieldValue, submitForm }) => {
        const hasDiscount = Number(values.discountApplied) > 0;
        const selectedOption = options.find((option) => option.id === values.paymentTypesID);
        const isCreditCard = isCreditCardOption(selectedOption);

        const handleDiscountAppliedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          handleChange(e);
          if (Number(e.target.value) === 0) {
            setFieldValue("discountOver", 0);
          }
        };

        const handleMaxInstallmentCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const nextMax = Math.max(1, Number(e.target.value) || 1);
          setFieldValue("maxInstallmentCount", nextMax);
          setFieldValue("interestRates", resizeInterestRates(values.interestRates ?? [], nextMax));
        };

        return (
          <MDBox p={3}>
            <MDTypography variant="h5" fontWeight="bold">
              {isEditing ? messages.formTitleEdit : messages.formTitleCreate}
            </MDTypography>

            <MDBox mt={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Autocomplete
                    fullWidth
                    disabled={isEditing || isLoadingAvailable}
                    options={options}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={options.find((o) => o.id === values.paymentTypesID) ?? null}
                    onChange={(_, newValue) => {
                      setFieldValue("paymentTypesID", newValue ? newValue.id : 0);
                      if (!isCreditCardOption(newValue)) {
                        setFieldValue("maxInstallmentCount", 1);
                        setFieldValue("minInstallmentAmount", 0);
                        setFieldValue("interestRates", [0]);
                        setFieldValue("monthlyInterestRates", "{}");
                      }
                    }}
                    loadingText={messages.loadingText}
                    noOptionsText={messages.noOptionsText}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={messages.paymentTypeLabel}
                        variant="standard"
                        error={!!errors.paymentTypesID}
                        helperText={errors.paymentTypesID as string | undefined}
                        slotProps={{ inputLabel: { shrink: true } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingAvailable && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }} display="flex" alignItems="flex-end">
                  <TextField
                    fullWidth
                    variant="standard"
                    name="discountApplied"
                    label={messages.discountAppliedLabel}
                    value={values.discountApplied}
                    onChange={handleDiscountAppliedChange}
                    error={!!errors.discountApplied}
                    helperText={errors.discountApplied as string | undefined}
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        inputComponent: CurrencyMaskInput as any,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    fullWidth
                    variant="standard"
                    name="discountOver"
                    label={messages.discountOverLabel}
                    value={values.discountOver}
                    onChange={handleChange}
                    disabled={!hasDiscount}
                    error={!!errors.discountOver}
                    helperText={errors.discountOver as string | undefined}
                    slotProps={{
                      inputLabel: { shrink: true },
                      input: {
                        inputComponent: CurrencyMaskInput as any,
                        startAdornment: (
                          <InputAdornment position="start">
                            <span style={{ fontSize: "0.75rem" }}>R$</span>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>

                {isCreditCard && (
                  <>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        variant="standard"
                        name="maxInstallmentCount"
                        type="number"
                        label={messages.maxInstallmentCountLabel}
                        value={values.maxInstallmentCount}
                        onChange={handleMaxInstallmentCountChange}
                        error={!!errors.maxInstallmentCount}
                        helperText={errors.maxInstallmentCount as string | undefined}
                        inputProps={{ min: 1, step: 1 }}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        variant="standard"
                        name="minInstallmentAmount"
                        label={messages.minInstallmentAmountLabel}
                        value={values.minInstallmentAmount}
                        onChange={handleChange}
                        error={!!errors.minInstallmentAmount}
                        helperText={errors.minInstallmentAmount as string | undefined}
                        slotProps={{
                          inputLabel: { shrink: true },
                          input: {
                            inputComponent: CurrencyMaskInput as any,
                            startAdornment: (
                              <InputAdornment position="start">
                                <span style={{ fontSize: "0.75rem" }}>R$</span>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <MDTypography variant="button" fontWeight="medium">
                        {messages.interestRatesSectionLabel}
                      </MDTypography>
                    </Grid>

                    {(values.interestRates ?? []).map((rate, index) => (
                      <Grid key={`interest-rate-${index + 1}`} size={{ xs: 12, sm: 4 }}>
                        <TextField
                          fullWidth
                          variant="standard"
                          name={`interestRates.${index}`}
                          label={messages.interestRateLabel.replace("{count}", String(index + 1))}
                          value={rate}
                          onChange={(e) => {
                            const nextRates = [...(values.interestRates ?? [])];
                            nextRates[index] = Number(e.target.value) || 0;
                            setFieldValue("interestRates", nextRates);
                          }}
                          error={!!(errors as any)[`interestRates.${index}`] || !!errors.interestRates}
                          helperText={
                            ((errors as any)[`interestRates.${index}`] as string | undefined) ||
                            (typeof errors.interestRates === "string"
                              ? errors.interestRates
                              : undefined)
                          }
                          slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                              inputComponent: CurrencyMaskInput as any,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <span style={{ fontSize: "0.75rem" }}>%</span>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>

              <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton variant="gradient" color="light" onClick={onCancel} disabled={isLoading}>
                  {messages.cancelButton}
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={submitForm}
                  disabled={isLoading}
                >
                  {messages.saveButton}
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        );
      }}
    </Formik>
  );
};

export default PaymentTypeForm;
